import { CHAT_PROXY_URL, ERROR_CODES } from './constants.js';
import { getBackendAuthHeaders } from '../../utils/backendAuth.js';
import { getDynamicNewQuestionPrompt } from './prompts.js';
import { state, updateMessage } from './chatUI.js';

export async function triggerSolutionLLM(questionText, dbAnswer, loadingMsg) {
    updateMessage(loadingMsg, "Official solution found. Rendering database content...");

    const unwrapRecord = (value) => {
        if (!value) return null;
        if (Array.isArray(value)) return value[0] || null;
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? (parsed[0] || null) : parsed;
            } catch (e) {
                return { answer: value };
            }
        }
        if (typeof value === 'object' && Array.isArray(value.data)) {
            return value.data[0] || null;
        }
        return value;
    };

    const parseOptions = (optionsValue) => {
        if (!optionsValue) return null;
        if (typeof optionsValue === 'object' && !Array.isArray(optionsValue)) {
            return optionsValue;
        }
        if (typeof optionsValue === 'string') {
            try {
                const parsed = JSON.parse(optionsValue);
                return parsed && typeof parsed === 'object' ? parsed : null;
            } catch (e) {
                return null;
            }
        }
        return null;
    };

    const record = unwrapRecord(dbAnswer) || {};
    const chapter = record.chapter ?? record.chapter_title ?? record.chapter_name ?? 'N/A';
    const question = record.question ?? record.Question ?? questionText ?? 'N/A';
    const answer = record.answer ?? record.Answer ?? record.answer_key ?? record.Answer_Key ?? 'N/A';
    const options = parseOptions(record.options ?? record.Options);

    loadingMsg.innerHTML = '';
    loadingMsg.className = 'msg bot';

    const content = document.createElement('div');
    content.style.cssText = 'display:flex; flex-direction:column; gap:10px;';

    const addSection = (label, value) => {
        const section = document.createElement('div');
        const labelEl = document.createElement('div');
        labelEl.innerHTML = `<strong>${label}</strong>`;
        const valueEl = document.createElement('div');
        valueEl.textContent = value;
        valueEl.style.whiteSpace = 'pre-wrap';
        section.appendChild(labelEl);
        section.appendChild(valueEl);
        content.appendChild(section);
    };

    addSection('Chapter', chapter);
    addSection('Question', question);

    const optionsSection = document.createElement('div');
    const optionsLabel = document.createElement('div');
    optionsLabel.innerHTML = '<strong>Options</strong>';
    optionsSection.appendChild(optionsLabel);

    if (options && typeof options === 'object' && Object.keys(options).length > 0) {
        const optionsList = document.createElement('div');
        optionsList.style.cssText = 'display:flex; flex-direction:column; gap:4px; margin-top:4px;';
        Object.entries(options).forEach(([key, value]) => {
            const optionRow = document.createElement('div');
            optionRow.textContent = `${key}. ${value}`;
            optionsList.appendChild(optionRow);
        });
        optionsSection.appendChild(optionsList);
    } else {
        const noneEl = document.createElement('div');
        noneEl.textContent = 'No options available.';
        noneEl.style.whiteSpace = 'pre-wrap';
        optionsSection.appendChild(noneEl);
    }

    content.appendChild(optionsSection);
    addSection('Answer', answer);

    loadingMsg.appendChild(content);

    const area = document.getElementById('messages-area');
    if (area) {
        area.scrollTop = area.scrollHeight;
    }

    const responseText = [
        `Chapter: ${chapter}`,
        `Question: ${question}`,
        `Options: ${options && typeof options === 'object' && Object.keys(options).length > 0 ? Object.entries(options).map(([key, value]) => `${key}. ${value}`).join(' | ') : 'No options available.'}`,
        `Answer: ${answer}`,
    ].join('\n');

    return responseText;
}

export async function triggerNewQuestionLLM(data, loadingMsg) {
    updateMessage(loadingMsg, "Generating new practice question...");

    const basePrompt = getDynamicNewQuestionPrompt(data);
    const langDirective = state.outputLanguage === 'zh'
        ? "[SYSTEM DIRECTIVE]: Respond EXCLUSIVELY in Traditional Chinese."
        : "[SYSTEM DIRECTIVE]: Respond EXCLUSIVELY in American English.";

    const aiPrompt = `${langDirective}\n\n${basePrompt}`;

    const reqObj2 = { message: aiPrompt, mode: "chat" };
    try {
        const aiRes = await fetch(CHAT_PROXY_URL, {
            method: "POST",
            headers: await getBackendAuthHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify(reqObj2)
        });
        const finalData = await aiRes.json();

        let jsonStr = (finalData.textResponse || "").replace(/^```json\s*/i, '').replace(/```\s*$/i, '').trim();
        let parsedJSON = null;
        try {
            parsedJSON = JSON.parse(jsonStr);
        } catch (parseError) {
            updateMessage(loadingMsg, `[System] ERR-202: ${ERROR_CODES['ERR-202']}.`);
            return;
        }

        loadingMsg.innerHTML = '';
        loadingMsg.className = 'msg bot';

        const qBlock = document.createElement('div');
        qBlock.innerHTML = `<strong>New_Question</strong><br><br>${(parsedJSON.Question || "No Question Provided").replace(/\n/g, '<br>')}`;

        let optionsBlock = null;
        if (parsedJSON.Options) {
            optionsBlock = document.createElement('div');
            optionsBlock.style.cssText = 'margin-top:10px; padding:8px 10px; background:#f9f3ff; border-left:3px solid #b088d0; border-radius:4px; font-size:13px;';
            let optsHtml = '';
            Object.keys(parsedJSON.Options).forEach(k => {
                optsHtml += `<div style="padding:3px 0;"><strong>${k}.</strong> ${parsedJSON.Options[k]}</div>`;
            });
            optionsBlock.innerHTML = optsHtml;
        }

        const btnRow = document.createElement('div');
        btnRow.style.cssText = 'display:flex; gap:10px; align-items:center; margin-top:10px; flex-wrap:wrap;';

        const tglBtn = document.createElement('button');
        tglBtn.className = 'qc-toggle-btn';
        tglBtn.innerText = 'Show Solution';

        const ansBox = document.createElement('div');
        ansBox.className = 'ans-box';
        let ansInner = `<strong>Answer: ${parsedJSON.Answer_Key || ''}</strong><br><br>`;
        ansInner += parsedJSON.Explanation || '';
        ansBox.innerHTML = ansInner.replace(/\n/g, '<br>');

        tglBtn.onclick = () => {
            if (ansBox.style.display === 'block') {
                ansBox.style.display = 'none';
                tglBtn.innerText = 'Show Solution';
            } else {
                ansBox.style.display = 'block';
                tglBtn.innerText = 'Hide Solution';
            }
        };

        btnRow.appendChild(tglBtn);

        loadingMsg.appendChild(qBlock);
        if (optionsBlock) loadingMsg.appendChild(optionsBlock);
        loadingMsg.appendChild(btnRow);
        loadingMsg.appendChild(ansBox);

        const area = document.getElementById('messages-area');
        area.scrollTop = area.scrollHeight;

        return finalData.textResponse || JSON.stringify(parsedJSON);

    } catch (err) { updateMessage(loadingMsg, `[System] ERR-301: ${ERROR_CODES['ERR-301']} (${err.message})`); return `[System] ERR-301: ${ERROR_CODES['ERR-301']} (${err.message})`; }
}

export function renderCarouselSelection(resultsArray, loadingMsg, sourceCmd) {
    const area = document.getElementById('messages-area');

    const wrapper = document.createElement('div');
    wrapper.className = 'carousel-wrapper';
    wrapper.style.width = '100%';

    const container = document.createElement('div');
    container.className = 'carousel-container';
    container.style.justifyContent = 'center';
    container.style.overflowX = 'hidden';
    container.style.padding = '10px 0';

    let currentIndex = 0;

    const navContainer = document.createElement('div');
    navContainer.className = 'carousel-nav-container';

    const leftArrow = document.createElement('button');
    leftArrow.className = 'carousel-arrow';
    leftArrow.innerText = '<';

    const rightArrow = document.createElement('button');
    rightArrow.className = 'carousel-arrow';
    rightArrow.innerText = '>';

    const progressBarContainer = document.createElement('div');
    progressBarContainer.className = 'carousel-progress-bg';
    const progressBar = document.createElement('div');
    progressBar.className = 'carousel-progress-bar';
    progressBarContainer.appendChild(progressBar);

    navContainer.appendChild(leftArrow);
    navContainer.appendChild(progressBarContainer);
    navContainer.appendChild(rightArrow);

    const card = document.createElement('div');
    card.className = 'carousel-card';
    card.style.margin = '0';
    card.style.minWidth = '260px';
    card.style.userSelect = 'none';

    let startX = 0;
    let isDragging = false;

    const handleTouchStart = (e) => {
        startX = e.touches ? e.touches[0].clientX : e.clientX;
        isDragging = true;
    };

    const handleTouchEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;
        const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
        const deltaX = endX - startX;

        if (deltaX > 50) {
            leftArrow.click();
        } else if (deltaX < -50) {
            rightArrow.click();
        } else if (Math.abs(deltaX) < 10) {
            wrapper.remove();
            const item = resultsArray[currentIndex];
            if (sourceCmd === '@newquestion') {
                triggerNewQuestionLLM(item, loadingMsg);
            } else {
                // pass full item so renderer can access chapter/options
                triggerSolutionLLM(item.question, item, loadingMsg);
            }
        }
    };

    card.addEventListener('touchstart', handleTouchStart, { passive: true });
    card.addEventListener('touchend', handleTouchEnd);
    card.addEventListener('mousedown', handleTouchStart);
    card.addEventListener('mouseup', handleTouchEnd);
    card.addEventListener('mouseleave', () => isDragging = false);

    const updateCard = () => {
        const item = resultsArray[currentIndex];
        const qPreview = item.question ? (item.question.length > 50 ? item.question.substring(0, 50) + '...' : item.question) : "N/A";

        card.innerHTML = `
            <div><strong>Subject:</strong> ${item.subject || 'Unknown'}</div>
            <div><strong>Chapter:</strong> ${item.chapter || 'Unknown'} | <strong>Category:</strong> ${item.q_category ?? '*'} | <strong>#:</strong> ${item.number ?? '*'}</div>
            <div><strong>Preview:</strong> ${qPreview}</div>
        `;

        const progressPercentage = ((currentIndex + 1) / resultsArray.length) * 100;
        progressBar.style.width = `${progressPercentage}%`;

        card.style.opacity = '0.5';
        card.style.transform = 'scale(0.98)';
        setTimeout(() => {
            card.style.opacity = '1';
            card.style.transform = 'scale(1)';
        }, 50);
    };

    leftArrow.onclick = () => {
        currentIndex = (currentIndex - 1 + resultsArray.length) % resultsArray.length;
        updateCard();
    };

    rightArrow.onclick = () => {
        currentIndex = (currentIndex + 1) % resultsArray.length;
        updateCard();
    };

    updateCard();

    container.appendChild(card);
    wrapper.appendChild(container);
    wrapper.appendChild(navContainer);

    area.appendChild(wrapper);
    area.scrollTop = area.scrollHeight;
}
